import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../../utils/api';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim().replace(/\/$/, '');

const ChangePasswordForm = () => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.oldPassword) {
      newErrors.oldPassword = 'Current password is required';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/Admin/change-password`,
        'POST',
        {
          OldPassword: formData.oldPassword,
          NewPassword: formData.newPassword,
          ConfirmPassword: formData.confirmPassword
        }
      );

      if (response?.success) {
        toast.success('Password changed successfully!');
        setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setErrors({});
      } else {
        toast.error(response?.message || 'Failed to change password.');
      }
    } catch (error) {
      console.error('Change password error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '12px 14px',
    border: `2px solid ${hasError ? '#ef4444' : '#e5e7eb'}`,
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none'
  });

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      backgroundColor: '#f8f9fa',
      padding: '20px'
    }}>
      <ToastContainer position="top-center" autoClose={3000} theme="light" />
      
      <div style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        padding: '40px 32px',
        border: '1px solid #e5e7eb'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            margin: '0 auto 12px',
            backgroundColor: '#F89423',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-lock" style={{ color: 'white', fontSize: '24px' }}></i>
          </div>
          <h2 style={{ 
            margin: 0, 
            color: '#1f2937', 
            fontSize: '24px', 
            fontWeight: '700' 
          }}>
            Change Password
          </h2>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '14px' }}>
            Update your account password
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          {/* Current Password */}
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#374151',
              fontSize: '14px'
            }}>
              Current Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="password"
              name="oldPassword"
              className="form-control"
              value={formData.oldPassword}
              onChange={handleChange}
              placeholder="Enter current password"
              disabled={loading}
              style={inputStyle(errors.oldPassword)}
              onFocus={(e) => e.target.style.borderColor = '#F89423'}
              onBlur={(e) => {
                if (!errors.oldPassword) e.target.style.borderColor = '#e5e7eb';
              }}
            />
            {errors.oldPassword && (
              <small style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.oldPassword}
              </small>
            )}
          </div>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

          {/* New Password */}
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#374151',
              fontSize: '14px'
            }}>
              New Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="password"
              name="newPassword"
              className="form-control"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
              disabled={loading}
              style={inputStyle(errors.newPassword)}
              onFocus={(e) => e.target.style.borderColor = '#F89423'}
              onBlur={(e) => {
                if (!errors.newPassword) e.target.style.borderColor = '#e5e7eb';
              }}
            />
            {errors.newPassword && (
              <small style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.newPassword}
              </small>
            )}
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '28px' }}>
            <label className="form-label" style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#374151',
              fontSize: '14px'
            }}>
              Confirm New Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="password"
              name="confirmPassword"
              className="form-control"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              disabled={loading}
              style={inputStyle(errors.confirmPassword)}
              onFocus={(e) => e.target.style.borderColor = '#F89423'}
              onBlur={(e) => {
                if (!errors.confirmPassword) e.target.style.borderColor = '#e5e7eb';
              }}
            />
            {errors.confirmPassword && (
              <small style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.confirmPassword}
              </small>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#F89423',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '15px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.8 : 1,
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#ea7c1f'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#F89423'; }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Update Password
              </>
            )}
          </button>
        </form>

        {/* Footer - Back to Home */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px', 
          paddingTop: '20px', 
          borderTop: '1px solid #e5e7eb',
          fontSize: '13px',
          color: '#6b7280'
        }}>
          <p style={{ margin: 0 }}>
            <Link to="/" style={{ color: '#F89423', textDecoration: 'none' }}>
              ← Back to Home
            </Link>
          </p>
        </div>
      </div>

      {/* Inline Styles */}
      <style>{`
        .form-control:focus {
          border-color: #F89423 !important;
          box-shadow: 0 0 0 3px rgba(248, 148, 35, 0.15);
        }
        @media (max-width: 480px) {
          .login-card { padding: 32px 24px; }
        }
      `}</style>
    </div>
  );
};

export default ChangePasswordForm;